import React, { useState, useEffect } from 'react';
import { DevicePhoneMobileIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import SupportForm from '../support/SupportForm';

const MFAChallenge = ({ onVerify, onCancel, loading = false }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    if (hasSubmitted || loading) {
      return;
    }

    setError('');
    setHasSubmitted(true);
    onVerify(code);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    if (error) setError('');
    // Reset submission state when user changes code
    if (hasSubmitted) setHasSubmitted(false);
  };

  // Auto-submit when 6 digits are entered (only once)
  useEffect(() => {
    if (code.length === 6 && !loading && !hasSubmitted) {
      handleSubmit(new Event('submit'));
    }
  }, [code, loading, hasSubmitted]);

  return (
    <div className="min-h-screen bg-secondary-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <ShieldCheckIcon className="w-8 h-8 text-primary-600" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-secondary-900">
          Two-Factor Authentication
        </h2>
        <p className="mt-2 text-center text-sm text-secondary-600">
          Enter the verification code from your authenticator app
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-secondary-700 mb-2">
                Verification Code
              </label>
              <div className="relative">
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className="block w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  disabled={loading}
                  autoFocus
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <DevicePhoneMobileIcon className="w-5 h-5 text-secondary-400" />
                </div>
              </div>
              <p className="mt-2 text-xs text-secondary-500">
                Open your authenticator app and enter the 6-digit code
              </p>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="xs" className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </button>
              
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="flex-1 flex justify-center py-2 px-4 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-secondary-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-secondary-500">Having trouble?</span>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="text-xs text-secondary-500">
                If you don't have access to your authenticator app,{' '}
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => setShowSupportForm(true)}
                >
                  contact support
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      <SupportForm 
        isOpen={showSupportForm} 
        onClose={() => setShowSupportForm(false)}
      />
    </div>
  );
};

export default MFAChallenge;